import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';

import { Flags } from 'constants/flags';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { colors } from 'styles/theme/colors';
import LinkIcon from 'svg/LinkIcon';

type Tab = {
  key: string;
  label: string;
  subtitle: string;
  episodeId: string;
  locationEpisodeId: string;
  createdAt: string;
};

const LinkedRecordsBanner = () => {
  const { locationEpisode } = useSelectedLocationEpisode();
  const { episode } = useEpisode();
  const { profile } = useProfile();
  const navigate = useNavigate();

  const tabs = useMemo(() => {
    if (!locationEpisode || !episode) return [];
    if (!locationEpisode.owner.client.hasFlag(Flags.SharedRecords)) return [];
    if (profile.canActAsManager && !profile.canActAsProvider) return [];
    if (locationEpisode.sharedRecords.length === 0) return [];
    if (!locationEpisode.latest || locationEpisode.archived) return [];

    const currentTab: Tab = {
      key: 'current',
      label: `${locationEpisode.owner.client.name} Record`,
      subtitle: locationEpisode.owner.groupType?.displayName ?? '',
      episodeId: episode.id,
      locationEpisodeId: locationEpisode.id,
      createdAt: locationEpisode.createdAt ?? '',
    };

    const linkedTabs: Tab[] = locationEpisode.sharedRecords.map((sr) => ({
      key: sr.id,
      label: `${sr.owner.client.name} Record`,
      subtitle: sr.owner.groupType?.displayName ?? '',
      episodeId: sr.episodeId,
      locationEpisodeId: sr.id,
      createdAt: sr.createdAt,
    }));

    return [currentTab, ...linkedTabs].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [locationEpisode, episode, profile]);

  if (tabs.length === 0) return null;

  const linkedCount = tabs.length;

  return (
    <Banner data-cy='linked-records-banner'>
      <TabsRow>
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            $active={tab.episodeId === episode?.id}
            onClick={() => navigate(`/patients/${tab.episodeId}?locationEpisodeId=${tab.locationEpisodeId}`)}>
            <TabLabel>{tab.label}</TabLabel>
            {tab.subtitle && <TabSubtitle>{tab.subtitle}</TabSubtitle>}
          </TabButton>
        ))}
      </TabsRow>
      <TabsFill>
        <LinkedInfo>
          <LinkIcon width={14} height={14} color={colors.black75} />
          <LinkedText>
            {linkedCount} shared patient {linkedCount === 1 ? 'record' : 'records'}
          </LinkedText>
        </LinkedInfo>
      </TabsFill>
    </Banner>
  );
};

export default LinkedRecordsBanner;

const Banner = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0;
  background-color: ${colors.black05};
`;

const TabsRow = styled.div`
  display: flex;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-right: 1px solid ${colors.black10};
  border-bottom: ${({ $active }) => ($active ? 'none' : `1px solid ${colors.black10}`)};
  cursor: pointer;
  padding: 12px 24px;
  border-radius: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ $active }) => ($active ? '#fff' : colors.black10)};
  }
`;

const TabsFill = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: stretch;
  border-bottom: 1px solid ${colors.black10};
`;

const TabLabel = styled.div`
  font-size: 12px;
  color: ${colors.black};
`;

const TabSubtitle = styled.div`
  font-size: 10px;
  color: ${colors.black};
`;

const LinkedInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 24px;

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 0 56px;
  }
`;

const LinkedText = styled.span`
  font-size: 10px;
  color: ${colors.black75};
`;
